import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { WorkflowIndicator } from '../components/WorkflowIndicator'
import { ReviewDetailsPanel } from '../components/ReviewDetailsPanel'
import { ItemValidationBadges } from '../components/ItemValidationBadges'

const box = { background: '#161d31', borderRadius: 12, padding: 16, marginBottom: 16, color: '#f8fafc' }
const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 14 }
const subtleBtn = { ...btn, background: '#334155' }
const ghostBtn = { ...btn, background: '#1e293b', color: '#cbd5e1' }
const input = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: 'white' }
const label = { display: 'block', fontSize: 14, color: '#94a3b8', marginBottom: 6 }

const bucketOrder = ['ready', 'needs_review', 'needs_conversion', 'duplicate', 'broken']
const bucketTone = {
  ready: { bg: '#052e16', fg: '#86efac' },
  needs_review: { bg: '#3f2c06', fg: '#fcd34d' },
  needs_conversion: { bg: '#3b0764', fg: '#d8b4fe' },
  duplicate: { bg: '#172554', fg: '#93c5fd' },
  broken: { bg: '#450a0a', fg: '#fca5a5' },
}

export default function ImportsPageRefactored() {
  // State
  const [runs, setRuns] = useState([])
  const [selectedRun, setSelectedRun] = useState(null)
  const [items, setItems] = useState([])
  const [countsByBucket, setCountsByBucket] = useState({})
  const [countsByReviewStatus, setCountsByReviewStatus] = useState({})
  const [selectedItemId, setSelectedItemId] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [duplicateSuggestions, setDuplicateSuggestions] = useState([])
  const [categories, setCategories] = useState([])
  const [vtneDomains, setVtneDomains] = useState([])
  const [triageFilter, setTriageFilter] = useState('')
  const [reviewFilter, setReviewFilter] = useState('pending')
  const [workMode, setWorkMode] = useState('all_pending')
  const [lastSelectedByMode, setLastSelectedByMode] = useState({})
  const [queueSearch, setQueueSearch] = useState('')
  const [bulkCategoryId, setBulkCategoryId] = useState('')
  const [bulkVtneDomainId, setBulkVtneDomainId] = useState('')
  const [uploading, setUploading] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [bulkWorking, setBulkWorking] = useState(false)
  const [approvingAllReady, setApprovingAllReady] = useState(false)
  const [exportingPacket, setExportingPacket] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  // Effects
  useEffect(() => {
    loadRuns()
    loadReferenceData()
  }, [])

  useEffect(() => {
    if (selectedRun?.id) loadItems(selectedRun.id)
  }, [selectedRun, triageFilter, reviewFilter])

  useEffect(() => {
    if (selectedItemId) loadItemDetail(selectedItemId)
  }, [selectedItemId])

  const modeFilteredItems = useMemo(() => {
    switch (workMode) {
      case 'ready':
        return items.filter(item => item.triage_bucket === 'ready')
      case 'needs_review':
        return items.filter(item => item.triage_bucket === 'needs_review')
      case 'needs_conversion':
        return items.filter(item => item.triage_bucket === 'needs_conversion')
      case 'broken':
        return items.filter(item => item.triage_bucket === 'broken')
      default:
        return items
    }
  }, [items, workMode])

  const visibleItems = useMemo(() => {
    const q = queueSearch.trim().toLowerCase()
    if (!q) return modeFilteredItems
    return modeFilteredItems.filter(item => `${item.proposed_admin_title || ''} ${item.proposed_stem || ''}`.toLowerCase().includes(q))
  }, [modeFilteredItems, queueSearch])

  useEffect(() => {
    if (selectedItemId) {
      setLastSelectedByMode(current => current[workMode] === selectedItemId ? current : { ...current, [workMode]: selectedItemId })
    }
  }, [selectedItemId, workMode])

  useEffect(() => {
    if (visibleItems.length === 0) {
      setSelectedItemId(null)
      return
    }
    const preferredId = lastSelectedByMode[workMode]
    if (preferredId && visibleItems.some(item => item.id === preferredId) && selectedItemId !== preferredId) {
      setSelectedItemId(preferredId)
      return
    }
    if (!visibleItems.some(item => item.id === selectedItemId)) {
      setSelectedItemId(visibleItems[0].id)
    }
  }, [visibleItems, selectedItemId, workMode, lastSelectedByMode])

  // Keyboard shortcuts for faster workflow
  useEffect(() => {
    function handleKeyDown(e) {
      if (!selectedItem || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

      const itemIndex = visibleItems.findIndex(i => i.id === selectedItemId)
      if (itemIndex === -1) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          if (itemIndex < visibleItems.length - 1) setSelectedItemId(visibleItems[itemIndex + 1].id)
          break
        case 'ArrowUp':
          e.preventDefault()
          if (itemIndex > 0) setSelectedItemId(visibleItems[itemIndex - 1].id)
          break
        case 'a':
        case 'A':
          e.preventDefault()
          if (e.ctrlKey || e.metaKey) return
          approveItem(selectedItemId)
          break
        case 'r':
        case 'R':
          e.preventDefault()
          if (e.ctrlKey || e.metaKey) return
          rejectItem(selectedItemId)
          break
        case 'e':
        case 'E':
          e.preventDefault()
          if (e.ctrlKey || e.metaKey) return
          escalateItem(selectedItemId)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedItemId, visibleItems, selectedItem])

  // Data loading
  async function loadRuns() {
    const rows = await api.importRuns()
    setRuns(rows)
    if (rows.length && !selectedRun) setSelectedRun(rows[0])
  }

  async function loadReferenceData() {
    const [categoryRows, domainRows] = await Promise.all([api.categories(), api.vtneDomains()])
    setCategories(categoryRows)
    setVtneDomains(domainRows)
  }

  async function loadItems(runId) {
    if (!runId) return []
    const data = await api.importItems(runId, { triage_bucket: triageFilter, review_status: reviewFilter })
    setItems(data.items)
    setCountsByBucket(data.counts_by_bucket || {})
    setCountsByReviewStatus(data.counts_by_review_status || {})
    const validSelectedIds = selectedIds.filter(id => data.items.some(item => item.id === id))
    setSelectedIds(validSelectedIds)
    if (data.items.length === 0) {
      setSelectedItemId(null)
      setSelectedItem(null)
    } else if (data.items.length > 0 && !data.items.some(i => i.id === selectedItemId)) {
      setSelectedItemId(data.items[0].id)
    }
    return data.items
  }

  async function loadItemDetail(itemId) {
    if (!itemId) return
    const item = await api.importItem(itemId)
    setSelectedItem(item)
    const dupes = await api.duplicateSuggestions(itemId)
    setDuplicateSuggestions(dupes)
  }

  function getNextVisibleItemId(currentId) {
    const itemIndex = visibleItems.findIndex(i => i.id === currentId)
    if (itemIndex === -1) return visibleItems[0]?.id || null
    return visibleItems[itemIndex + 1]?.id || visibleItems[itemIndex - 1]?.id || null
  }

  function jumpToFirstVisibleItem() {
    if (visibleItems.length > 0) {
      setSelectedItemId(visibleItems[0].id)
    }
  }

  function syncSelectionAfterRefresh(refreshedItems, preferredId = null) {
    if (preferredId && refreshedItems.some(item => item.id === preferredId)) {
      setSelectedItemId(preferredId)
      return
    }
    setSelectedItemId(refreshedItems[0]?.id || null)
  }

  // Actions
  async function onUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setMessage('')
    setError('')
    try {
      const run = await api.uploadGift(file)
      await loadRuns()
      setSelectedRun(run)
      setSelectedIds([])
      setMessage(`✓ Uploaded ${file.name}`)
    } catch (err) {
      setError(String(err))
    } finally {
      setUploading(false)
    }
  }

  async function parseRun() {
    if (!selectedRun) return
    setParsing(true)
    setMessage('')
    setError('')
    try {
      const run = await api.parseImport(selectedRun.id)
      setSelectedRun(run)
      setSelectedIds([])
      await loadItems(run.id)
      await loadRuns()
      setMessage(`✓ Parsed ${run.file_name} into staging`)
    } catch (err) {
      setError(String(err))
    } finally {
      setParsing(false)
    }
  }

  async function saveItemDetails(form, options = {}) {
    if (!selectedItemId) return
    const nextId = options.advance ? getNextVisibleItemId(selectedItemId) : null
    setSaving(true)
    setMessage('')
    setError('')
    try {
      const payload = { ...form }
      if (payload.proposed_category_id === '') payload.proposed_category_id = null
      if (payload.proposed_vtne_domain_id === '') payload.proposed_vtne_domain_id = null
      if (payload.duplicate_of_question_id === '') payload.duplicate_of_question_id = null
      const updated = await api.updateImportItem(selectedItemId, payload)
      setSelectedItem(updated)
      const refreshedItems = await loadItems(selectedRun.id)
      if (options.advance) syncSelectionAfterRefresh(refreshedItems, nextId)
      setMessage(options.advance ? '✓ Saved changes and moved to next item' : '✓ Saved changes')
    } catch (err) {
      setError(String(err))
    } finally {
      setSaving(false)
    }
  }

  async function approveItem(id, options = {}) {
    const nextId = options.advance ? getNextVisibleItemId(id) : null
    setMessage('')
    setError('')
    try {
      await api.approveImportItem(id)
      const refreshedItems = await loadItems(selectedRun.id)
      await loadRuns()
      if (options.advance) syncSelectionAfterRefresh(refreshedItems, nextId)
      setMessage(options.advance ? '✓ Approved and moved to next item' : '✓ Approved into canonical bank')
    } catch (err) {
      setError(String(err))
    }
  }

  async function rejectItem(id, options = {}) {
    const nextId = options.advance ? getNextVisibleItemId(id) : null
    setMessage('')
    setError('')
    try {
      await api.rejectImportItem(id)
      const refreshedItems = await loadItems(selectedRun.id)
      await loadRuns()
      if (options.advance) syncSelectionAfterRefresh(refreshedItems, nextId)
      setMessage(options.advance ? '✓ Rejected and moved to next item' : '✓ Rejected staging item')
    } catch (err) {
      setError(String(err))
    }
  }

  async function escalateItem(id, options = {}) {
    const nextId = options.advance ? getNextVisibleItemId(id) : null
    setMessage('')
    setError('')
    try {
      await api.escalateImportItemToExpert(id)
      const refreshedItems = await loadItems(selectedRun.id)
      if (options.advance) syncSelectionAfterRefresh(refreshedItems, nextId)
      setMessage(options.advance ? '✓ Escalated and moved to next item' : '✓ Escalated for expert review')
    } catch (err) {
      setError(String(err))
    }
  }

  async function bulkAction(action, payload = {}) {
    if (!selectedRun || selectedIds.length === 0) return
    setBulkWorking(true)
    setMessage('')
    setError('')
    try {
      const result = await api.bulkImportItems(selectedRun.id, { action, item_ids: selectedIds, ...payload })
      await loadItems(selectedRun.id)
      await loadRuns()
      setMessage(`✓ ${action.replace(/_/g, ' ')}: updated ${result.updated_count} item(s)`)
    } catch (err) {
      setError(String(err))
    } finally {
      setBulkWorking(false)
    }
  }

  async function approveAllReady() {
    if (!selectedRun) return
    setApprovingAllReady(true)
    setMessage('')
    setError('')
    try {
      const result = await api.approveAllReadyImportItems(selectedRun.id, {})
      await loadItems(selectedRun.id)
      await loadRuns()
      setMessage(`✓ Approved ${result.approved_count} ready items`)
    } catch (err) {
      setError(String(err))
    } finally {
      setApprovingAllReady(false)
    }
  }

  async function exportReviewPacket() {
    const itemIds = selectedIds.length > 0 ? selectedIds : items.map(i => i.id)
    if (itemIds.length === 0) return
    setExportingPacket(true)
    setMessage('')
    setError('')
    try {
      const result = await api.bulkExportReviewPacket({
        item_ids: itemIds,
        title: selectedRun ? `${selectedRun.file_name} review packet` : 'review packet',
      })
      window.open(result.download_url, '_blank')
      setMessage(`✓ Exported ${result.item_count} item(s)`)
    } catch (err) {
      setError(String(err))
    } finally {
      setExportingPacket(false)
    }
  }

  function toggleSelected(id) {
    setSelectedIds(current => current.includes(id) ? current.filter(v => v !== id) : [...current, id])
  }

  function toggleSelectAll() {
    if (visibleItems.length > 0 && selectedIds.length === visibleItems.length && visibleItems.every(i => selectedIds.includes(i.id))) {
      setSelectedIds([])
    } else {
      setSelectedIds(visibleItems.map(i => i.id))
    }
  }

  // Derived state
  const effectiveSelectedIds = selectedIds.length > 0 ? selectedIds : (selectedItemId ? [selectedItemId] : [])
  const allSelected = visibleItems.length > 0 && visibleItems.every(i => selectedIds.includes(i.id))
  const workflowStage = triageFilter ? 'triage' : reviewFilter !== 'pending' ? 'review' : 'parse'

  return (
    <div>
      {/* Workflow indicator */}
      {selectedRun && <WorkflowIndicator stage={workflowStage} stats={{ triage_count: Object.values(countsByBucket).reduce((a, b) => a + b, 0) }} />}

      {/* Status messages */}
      {message && <div style={{ ...box, background: '#052e16', color: '#86efac', marginBottom: 16 }}>{message}</div>}
      {error && <div style={{ ...box, background: '#450a0a', color: '#fca5a5', marginBottom: 16 }}>{error}</div>}

      {/* Main layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: 20 }}>
        {/* Left sidebar: Upload and import runs */}
        <div>
          <div style={box}>
            <h2 style={{ marginTop: 0 }}>Import GIFT</h2>
            <input
              type="file"
              accept=".gift,.txt"
              style={input}
              onChange={onUpload}
              disabled={uploading}
            />
            <div style={{ marginTop: 12, fontSize: 13, color: '#94a3b8' }}>
              {uploading ? 'Uploading…' : 'Upload a raw GIFT file from Moodle'}
            </div>
          </div>

          <div style={box}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Recent Imports</h3>
            {runs.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>No imports yet.</div>
            ) : (
              runs.map(run => {
                const isSelected = selectedRun?.id === run.id
                return (
                  <button
                    key={run.id}
                    onClick={() => setSelectedRun(run)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: 12,
                      marginBottom: 8,
                      border: isSelected ? '2px solid #4f46e5' : '1px solid #334155',
                      background: isSelected ? '#1e3a8a' : '#0f172a',
                      color: 'white',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: isSelected ? 700 : 500,
                    }}
                  >
                    <div style={{ fontSize: 14 }}>{run.file_name}</div>
                    <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 4 }}>
                      {run.item_count} items • {run.status}
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* Right: Main workflow area */}
        <div>
          {!selectedRun ? (
            <div style={box}>
              <h2 style={{ marginTop: 0 }}>Select an Import</h2>
              <p style={{ color: '#94a3b8' }}>Choose an import from the left, or upload a new GIFT file to begin.</p>
            </div>
          ) : (
            <>
              {/* Parse section */}
              {selectedRun.status === 'uploaded' && (
                <div style={box}>
                  <h2 style={{ marginTop: 0 }}>Step 1: Parse GIFT File</h2>
                  <p style={{ color: '#94a3b8', marginBottom: 16 }}>
                    Click the button below to parse the uploaded GIFT file into staging questions.
                  </p>
                  <button style={btn} onClick={parseRun} disabled={parsing}>
                    {parsing ? 'Parsing…' : '📄 Parse into Staging'}
                  </button>
                </div>
              )}

              {/* Triage & Review controls */}
              {selectedRun.status === 'parsed' && (
                <>
                  <div style={box}>
                    <h2 style={{ marginTop: 0 }}>Step 2: Triage & Review</h2>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                        {bucketOrder.map(bucket => {
                          const tone = bucketTone[bucket]
                          const active = triageFilter === bucket
                          const count = countsByBucket[bucket] || 0
                          return (
                            <button
                              key={bucket}
                              onClick={() => setTriageFilter(active ? '' : bucket)}
                              style={{
                                ...btn,
                                background: active ? tone.bg : '#0f172a',
                                color: active ? tone.fg : '#cbd5e1',
                                border: `2px solid ${active ? tone.fg : '#334155'}`,
                                padding: '10px 12px',
                                fontSize: 13,
                                textTransform: 'capitalize',
                              }}
                            >
                              {bucket.replace('_', ' ')} ({count})
                            </button>
                          )
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button style={subtleBtn} onClick={toggleSelectAll} disabled={items.length === 0}>
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </button>
                        <button
                          style={btn}
                          onClick={() => bulkAction('assign_category', { category_id: Number(bulkCategoryId) })}
                          disabled={bulkWorking || effectiveSelectedIds.length === 0 || !bulkCategoryId}
                        >
                          Assign Category
                        </button>
                        <button
                          style={btn}
                          onClick={() => bulkAction('assign_vtne_domain', { vtne_domain_id: Number(bulkVtneDomainId) })}
                          disabled={bulkWorking || effectiveSelectedIds.length === 0 || !bulkVtneDomainId}
                        >
                          Assign Domain
                        </button>
                        <button style={subtleBtn} onClick={approveAllReady} disabled={approvingAllReady}>
                          {approvingAllReady ? 'Approving…' : 'Approve All Ready'}
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={label}>Quick assign category</label>
                        <select style={input} value={bulkCategoryId} onChange={e => setBulkCategoryId(e.target.value)}>
                          <option value="">Choose category…</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={label}>Quick assign domain</label>
                        <select style={input} value={bulkVtneDomainId} onChange={e => setBulkVtneDomainId(e.target.value)}>
                          <option value="">Choose domain…</option>
                          {vtneDomains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: workMode === 'all_pending' ? '460px minmax(0, 1fr)' : '400px minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
                    {/* Queue pane */}
                    <div style={{ ...box, marginBottom: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <h3 style={{ margin: 0 }}>Review Queue ({visibleItems.length})</h3>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{items.length} total in current filter</div>
                      </div>

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        {[
                          ['all_pending', 'All Pending'],
                          ['ready', 'Ready'],
                          ['needs_review', 'Needs Review'],
                          ['needs_conversion', 'Needs Conversion'],
                          ['broken', 'Broken'],
                        ].map(([mode, label]) => {
                          const active = workMode === mode
                          return (
                            <button
                              key={mode}
                              onClick={() => setWorkMode(mode)}
                              style={{
                                ...ghostBtn,
                                background: active ? '#4f46e5' : '#1e293b',
                                color: 'white',
                                padding: '8px 10px',
                                fontSize: 12,
                              }}
                            >
                              {label}
                            </button>
                          )
                        })}
                      </div>

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        <button
                          style={{ ...subtleBtn, padding: '8px 10px', fontSize: 12 }}
                          onClick={jumpToFirstVisibleItem}
                          disabled={visibleItems.length === 0}
                        >
                          Jump to First
                        </button>
                        <button
                          style={{ ...subtleBtn, padding: '8px 10px', fontSize: 12 }}
                          onClick={() => {
                            const remembered = lastSelectedByMode[workMode]
                            if (remembered && visibleItems.some(item => item.id === remembered)) setSelectedItemId(remembered)
                          }}
                          disabled={!lastSelectedByMode[workMode] || !visibleItems.some(item => item.id === lastSelectedByMode[workMode])}
                        >
                          Continue Where I Left Off
                        </button>
                      </div>

                      <input
                        style={{ ...input, marginBottom: 12 }}
                        value={queueSearch}
                        onChange={e => setQueueSearch(e.target.value)}
                        placeholder="Search title or stem…"
                      />

                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
                        <span>Mode: {workMode.replace(/_/g, ' ')}</span>
                        <span>{visibleItems.length} visible</span>
                      </div>

                      {visibleItems.length === 0 ? (
                        <div style={{ color: '#94a3b8' }}>No items match current filters.</div>
                      ) : (
                        <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto', paddingRight: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {visibleItems.map(item => {
                            const isSelected = selectedItemId === item.id
                            const tone = bucketTone[item.triage_bucket]
                            return (
                              <button
                                key={item.id}
                                onClick={() => setSelectedItemId(item.id)}
                                style={{
                                  textAlign: 'left',
                                  padding: '10px 12px',
                                  background: isSelected ? '#273449' : '#0f172a',
                                  border: isSelected ? '2px solid #4f46e5' : '1px solid #334155',
                                  borderLeft: `6px solid ${isSelected ? '#818cf8' : tone.fg}`,
                                  borderRadius: 10,
                                  cursor: 'pointer',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, color: '#f8fafc', fontSize: 13, marginBottom: 4 }}>
                                      {item.proposed_admin_title || 'Untitled'}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.35, marginBottom: 6 }}>
                                      {(item.proposed_stem || '').slice(0, 90)}{(item.proposed_stem || '').length > 90 ? '…' : ''}
                                    </div>
                                    <ItemValidationBadges item={item} compact={true} />
                                  </div>
                                  <div style={{
                                    ...tone,
                                    padding: '3px 7px',
                                    borderRadius: 999,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    textTransform: 'capitalize',
                                  }}>
                                    {item.triage_bucket.replace(/_/g, ' ')}
                                  </div>
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Inspector pane */}
                    <div style={{ minWidth: 0 }}>
                      <ReviewDetailsPanel
                        item={selectedItem}
                        categories={categories}
                        vtneDomains={vtneDomains}
                        onSave={saveItemDetails}
                        onSaveAndNext={(form) => saveItemDetails(form, { advance: true })}
                        onApprove={approveItem}
                        onApproveAndNext={(id) => approveItem(id, { advance: true })}
                        onReject={rejectItem}
                        onRejectAndNext={(id) => rejectItem(id, { advance: true })}
                        onEscalate={escalateItem}
                        onEscalateAndNext={(id) => escalateItem(id, { advance: true })}
                        saving={saving}
                        queuePosition={visibleItems.findIndex(item => item.id === selectedItemId) + 1}
                        queueCount={visibleItems.length}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
